import { z } from 'zod';

import {
  type CaptionPosition,
  type CaptionStyle,
  captionStyleSchema,
  presetById,
} from './caption-styles';
import type { SpokenWord } from './payloads';

/**
 * A reel edit made in review: the reviewed reel cut into clips played in
 * order, text laid over it and subtitles from its voiceover. The web app
 * previews it and the API renders it from the same document, with the same
 * timing helpers below, so the preview and the render agree.
 *
 * Clip times (`in`, `out`) are seconds in the source reel; text times are
 * seconds on the edited timeline.
 */

export const REEL_EDIT_LIMITS = {
  /** Longest an edited reel can be. */
  maxSeconds: 180,
  /** Shortest a clip can be. */
  minClipSeconds: 0.3,
  maxClips: 60,
  maxTexts: 20,
} as const;

const POSITIONS = ['top', 'center', 'bottom'] as const satisfies CaptionPosition[];

export const timelineClipSchema = z.object({
  id: z.string().min(1).max(40),
  in: z.number().min(0),
  out: z.number().positive(),
  /** 0 mutes the clip, 1 is as recorded. */
  volume: z.number().min(0).max(2).default(1),
});

export const timelineTextSchema = z.object({
  id: z.string().min(1).max(40),
  /** Words in *asterisks* get the style's highlight colour. */
  text: z.string().trim().min(1).max(300),
  start: z.number().min(0),
  end: z.number().positive(),
  position: z.enum(POSITIONS),
  style: captionStyleSchema,
});

export const timelineSubtitlesSchema = z.object({
  enabled: z.boolean(),
  /** A few words at a time with the spoken one highlighted, or a line at a time. */
  mode: z.enum(['word', 'line']),
  position: z.enum(POSITIONS),
  style: captionStyleSchema,
});

export const timelineMusicSchema = z.object({
  assetId: z.uuid(),
  volume: z.number().min(0).max(1),
  /** Where in the track to start, in seconds. */
  offset: z.number().min(0).default(0),
});

export const reelTimelineSchema = z
  .object({
    version: z.literal(1),
    clips: z.array(timelineClipSchema).min(1).max(REEL_EDIT_LIMITS.maxClips),
    texts: z.array(timelineTextSchema).max(REEL_EDIT_LIMITS.maxTexts).default([]),
    subtitles: timelineSubtitlesSchema.optional(),
    music: timelineMusicSchema.optional(),
  })
  .superRefine((timeline, ctx) => {
    timeline.clips.forEach((clip, i) => {
      if (clip.out - clip.in < REEL_EDIT_LIMITS.minClipSeconds - 1e-6)
        ctx.addIssue({
          code: 'custom',
          path: ['clips', i],
          message: `Clips must be at least ${REEL_EDIT_LIMITS.minClipSeconds} seconds long.`,
        });
    });
    const duration = timelineDuration(timeline);
    if (duration > REEL_EDIT_LIMITS.maxSeconds + 1e-6)
      ctx.addIssue({
        code: 'custom',
        path: ['clips'],
        message: `An edited reel can be at most ${REEL_EDIT_LIMITS.maxSeconds} seconds long.`,
      });
    timeline.texts.forEach((text, i) => {
      if (text.end <= text.start)
        ctx.addIssue({
          code: 'custom',
          path: ['texts', i],
          message: 'Text must end after it starts.',
        });
      else if (text.start >= duration)
        ctx.addIssue({
          code: 'custom',
          path: ['texts', i],
          message: 'Text starts after the reel ends.',
        });
    });
  });

export type ReelTimeline = z.output<typeof reelTimelineSchema>;
export type TimelineClip = z.output<typeof timelineClipSchema>;
export type TimelineText = z.output<typeof timelineTextSchema>;
export type TimelineSubtitles = z.output<typeof timelineSubtitlesSchema>;

const round = (seconds: number) => Math.round(seconds * 1000) / 1000;

/** The reel as it reached review: one clip, the whole of it. */
export function initialTimeline(durationSeconds: number): ReelTimeline {
  return {
    version: 1,
    clips: [{ id: 'clip-1', in: 0, out: round(durationSeconds), volume: 1 }],
    texts: [],
  };
}

/** The look subtitles start with: white words, the spoken one in yellow. */
export function defaultSubtitles(): TimelineSubtitles {
  const style: CaptionStyle = presetById('tiktok-classic')!.style;
  return {
    enabled: true,
    mode: 'word',
    position: 'bottom',
    style: { ...style, highlight: { enabled: true, color: '#FFD84D' } },
  };
}

/** How long the edited reel runs. */
export const timelineDuration = (timeline: Pick<ReelTimeline, 'clips'>) =>
  round(timeline.clips.reduce((sum, clip) => sum + (clip.out - clip.in), 0));

/** Where each clip starts on the edited timeline. */
export function clipStarts(timeline: Pick<ReelTimeline, 'clips'>): number[] {
  let at = 0;
  return timeline.clips.map((clip) => {
    const start = at;
    at += clip.out - clip.in;
    return round(start);
  });
}

/**
 * The clip playing at `at` seconds on the edited timeline, and the time in the
 * source to show. Past the end, the last clip's last moment.
 */
export function timelineToSource(
  timeline: Pick<ReelTimeline, 'clips'>,
  at: number,
): { index: number; sourceTime: number } {
  let start = 0;
  for (const [index, clip] of timeline.clips.entries()) {
    const length = clip.out - clip.in;
    if (at < start + length || index === timeline.clips.length - 1) {
      const offset = Math.min(Math.max(at - start, 0), length);
      return { index, sourceTime: round(clip.in + offset) };
    }
    start += length;
  }
  return { index: 0, sourceTime: 0 };
}

/** Where a moment in clip `index`'s source shows on the edited timeline. */
export function sourceToTimeline(
  timeline: Pick<ReelTimeline, 'clips'>,
  index: number,
  sourceTime: number,
): number {
  const clip = timeline.clips[index];
  if (!clip) return timelineDuration(timeline);
  const offset = Math.min(Math.max(sourceTime - clip.in, 0), clip.out - clip.in);
  return round((clipStarts(timeline)[index] ?? 0) + offset);
}

/**
 * Word timings after keeping only `ranges` of the source, in that order:
 * words said inside a range move with it, the rest are cut.
 */
export function retimeWords(
  words: readonly SpokenWord[],
  ranges: readonly { in: number; out: number }[],
): SpokenWord[] {
  const out: SpokenWord[] = [];
  let offset = 0;
  for (const range of ranges) {
    for (const word of words) {
      if (word.start < range.in || word.start >= range.out) continue;
      out.push({
        text: word.text,
        start: round(word.start - range.in + offset),
        end: round(Math.min(word.end, range.out) - range.in + offset),
      });
    }
    offset += range.out - range.in;
  }
  return out;
}

/** A stretch of subtitles: its words, the one being said (word mode), and when it shows. */
export type SubtitleCue = {
  words: string[];
  /** Index into `words` of the word being said; null shows the line plain. */
  highlight: number | null;
  start: number;
  end: number;
};

/** A pause between words shorter than this keeps the subtitle up until the next word. */
const HOLD_GAP = 0.6;

/**
 * Subtitles for a voiceover: in word mode, three words at a time with the
 * spoken one highlighted; in line mode, six words at a time.
 */
export function subtitleCues(
  words: readonly SpokenWord[],
  mode: 'word' | 'line',
): SubtitleCue[] {
  const size = mode === 'word' ? 3 : 6;
  const cues: SubtitleCue[] = [];
  for (let at = 0; at < words.length; at += size) {
    const chunk = words.slice(at, at + size);
    const texts = chunk.map((word) => word.text);
    if (mode === 'line') {
      cues.push({
        words: texts,
        highlight: null,
        start: chunk[0]!.start,
        end: chunk.at(-1)!.end,
      });
      continue;
    }
    chunk.forEach((word, index) => {
      const next = chunk[index + 1] ?? words[at + size];
      const end = next && next.start - word.end < HOLD_GAP ? next.start : word.end;
      cues.push({ words: texts, highlight: index, start: word.start, end });
    });
  }
  return cues;
}

/** A cue as caption text, its spoken word in *asterisks* for the highlight colour. */
export function cueMarkup(cue: SubtitleCue): string {
  return cue.words
    .map((word, i) => {
      const plain = word.replaceAll('*', '');
      return i === cue.highlight && plain ? `*${plain}*` : plain;
    })
    .join(' ');
}

/** What shows at `at`: things whose [start, end) covers it. */
export const activeAt = <T extends { start: number; end: number }>(
  items: readonly T[],
  at: number,
): T[] => items.filter((item) => item.start <= at && at < item.end);
